import { useCallback, useRef } from 'react'

import { createApiCall } from 'services'
import { currentShareSurface } from 'utils/shareLink'

import type { TCertificateShareMethod } from '@repo/types'

/**
 * VRG-778 — record a share, and record an open of a shared link.
 *
 * ## Both calls are FIRE-AND-FORGET, and that is the design
 *
 * Neither is awaited by anything the user is waiting on, and neither rejects.
 * Two separate reasons:
 *
 *  - **The share.** `navigator.share()` needs the user's transient activation,
 *    which an `await` destroys on iOS Safari (see `utils/shareLink`). So the POST
 *    cannot come first; it goes out in parallel, after the sheet has opened.
 *    And a human passing a link to another human must not be able to fail
 *    because our analytics did.
 *  - **The visit.** It fires during the certificate page's render. A rejected
 *    promise there is an unhandled rejection in a page whose only job is to
 *    convince a stranger the item in their hand is real.
 *
 * `createApiCall` without `throwError` already resolves non-2xx responses rather
 * than rejecting, and the `.catch` below covers the network-level case. Belt and
 * braces on purpose: this is instrumentation, and instrumentation that can break
 * its host is worse than no instrumentation.
 *
 * ⚠️ `actor_kind` is NOT sent. The server derives it from the auth token plus an
 * ownership check, because a client-asserted actor is an unauthenticated claim —
 * and distinguishing the owner's own distribution from the distribution they got
 * for free is the whole point of the field. Do not add it here.
 */
export const useRecordCertificateShare = () => {
  return useCallback(
    (params: {
      certificateId: string
      shareToken: string
      method: TCertificateShareMethod
      /** For an owner share: which of their links went out. */
      viewSlug?: string
    }) => {
      const { certificateId, shareToken, method, viewSlug } = params
      if (!certificateId || !shareToken) return

      void createApiCall({
        method: 'POST',
        url: `/certificates/public/${certificateId}/share`,
        data: {
          surface: currentShareSurface(),
          method,
          share_token: shareToken,
          ...(viewSlug ? { view_slug: viewSlug } : {}),
        },
      }).catch(() => {
        // Deliberately silent. See the docblock: the share already happened.
      })
    },
    [],
  )
}

/**
 * Credit one open to the share whose `?s=` token this page load carried.
 *
 * ## The dedupe is per browser SESSION, and is best-effort by design
 *
 * A refresh would otherwise count again. The server deliberately does NOT
 * deduplicate, because doing so needs a per-visitor identifier — exactly the
 * behavioural data this feature's privacy posture avoids storing. So the dedupe
 * lives here, in `sessionStorage`, where it costs nothing and identifies nobody.
 *
 * What that honestly buys: one count per (token, browser session). It does not
 * survive a new tab, a different device, or private browsing, and a recipient who
 * strips the query string is never counted at all. Every one of those is an
 * UNDER-count, which is the safe direction for a metric — but `0` opens must
 * never be read as "nobody looked".
 *
 * A `sessionStorage` accessor can throw (private mode, blocked site data), so
 * every access is guarded and a failure degrades to "count it" rather than
 * "skip it" — losing the dedupe is cheaper than losing the signal.
 */
export const useRecordCertificateShareVisit = () => {
  // Guards against a double-fire inside ONE mount (React 18 StrictMode runs
  // effects twice in development), which `sessionStorage` alone would not catch
  // if both fired before the first write landed.
  const firedRef = useRef<Set<string>>(new Set())

  return useCallback((certificateId: string, shareToken: string | null) => {
    if (!certificateId || !shareToken) return

    const key = `vera.share.visit.${certificateId}.${shareToken}`
    if (firedRef.current.has(key)) return
    firedRef.current.add(key)

    try {
      if (sessionStorage.getItem(key)) return
      sessionStorage.setItem(key, '1')
    } catch {
      // Unavailable or blocked. Fall through and count it — see the docblock.
    }

    void createApiCall({
      method: 'POST',
      url: `/certificates/public/${certificateId}/share/${encodeURIComponent(
        shareToken,
      )}/visit`,
    }).catch(() => {
      // Silent: this runs during a render of the page a stranger is reading.
    })
  }, [])
}
